import { Renderer } from './Renderer.js';
import { Scene } from './Scene.js';
import { Camera } from './Camera.js';
import { GameLoop } from './GameLoop.js';
import { WebSocketClient } from '../network/WebSocketClient.js';
import { MessageHandler } from '../network/MessageHandler.js';
import { GameState } from '../state/GameState.js';
import { InputManager } from '../input/InputManager.js';
import { HUD } from '../ui/HUD.js';

// Game wires every subsystem together. main.js just calls new Game().init().
export class Game {
  constructor() {
    this.gameState = new GameState();
    this.hud = new HUD();
    this.input = new InputManager();

    this.network = new WebSocketClient((msg) => this.messageHandler.handle(msg));
    this.messageHandler = new MessageHandler(this.gameState);
    this.messageHandler.setupDefaultHandlers();
  }

  async init() {
    const container = document.getElementById('game-container');
    this.renderer = new Renderer(container);
    this.scene = new Scene();
    this.messageHandler.setScene(this.scene);
    this.camera = new Camera();

    this.input.enable();

    // Restore the user's preferred display name from localStorage and
    // wire the lobby input to update both localStorage and the server.
    this._setupUsername();

    this.network.setConnectionChangeHandler((connected) => {
      this.hud.setConnected(connected);
      // (Re)broadcast the chosen display name whenever the WS comes up,
      // including the initial open and any auto-reconnect. The server
      // assigns a Rider_NNNN placeholder otherwise.
      if (connected) this._sendDisplayName();
    });
    this.network.connect();

    this.hud.onJoinClicked(() => this._joinQueue());
    this.hud.onStartNowClicked(() => this._startQueuedNow());
    this.hud.onStartOverClicked(() => this._returnToLobby());
    this.hud.onPlayVsAIClicked(() => this._startVsAI());
    this.hud.onStopSpectatingClicked(() => this._stopSpectating());

    // Server pushes active_games on every room create/end/spectator
    // change. We just render whatever the latest push contained — no
    // polling, no Refresh button needed.
    this.messageHandler.setActiveGamesHandler((p) => {
      this.hud.renderLiveGames(p.games || [], (gameId) => this._spectate(gameId));
    });

    // Optional leaderboard: only fires if the server has LEADERBOARD_PATH
    // set. The HUD's render call also handles hiding the empty state.
    this.messageHandler.setLeaderboardHandler((p) => {
      this.hud.renderLeaderboard(p);
    });

    this.loop = new GameLoop({
      renderer: this.renderer,
      scene: this.scene,
      camera: this.camera,
      gameState: this.gameState,
      input: this.input,
      hud: this.hud,
      network: this.network,
    });
    this.loop.start();
  }

  _joinQueue() {
    if (!this.network.isConnected()) return;
    const { mode, trackId } = this.hud.getMultiplayerSelection();
    this.network.send('join_queue', { mode, trackId });
    this.hud.showWaitingForRacers(true);
  }

  _startVsAI() {
    if (!this.network.isConnected()) return;
    const { mode, trackId } = this.hud.getSingleplayerSelection();
    this.network.send('start_vs_ai', {
      difficulty: this.hud.getSelectedDifficulty(),
      aiCount: 3,
      mode,
      trackId,
    });
  }

  _startQueuedNow() {
    if (!this.network.isConnected()) return;
    this.network.send('start_queued_now', {});
  }

  // Loads the saved username from localStorage and wires the lobby input
  // to push edits back to localStorage + the server. Returns silently on
  // browsers without localStorage support.
  _setupUsername() {
    this._usernameInput = document.getElementById('username-input');
    if (!this._usernameInput) return;
    let saved = '';
    try {
      saved = localStorage.getItem('snowboard.username') || '';
    } catch {
      // localStorage might be blocked (private mode); fine.
    }
    if (saved) this._usernameInput.value = saved;

    this._usernameInput.addEventListener('input', () => {
      const v = this._usernameInput.value;
      try { localStorage.setItem('snowboard.username', v); } catch {}
      this._sendDisplayName();
    });
  }

  _sendDisplayName() {
    const v = (this._usernameInput?.value || '').trim();
    if (!v) return;
    this.network.send('set_display_name', { name: v });
  }

  // Reset back to the lobby after a race ends. Tells the server we've
  // left, clears local race state, hides the podium, and lets the user
  // pick a mode again.
  _returnToLobby() {
    this.network.send('leave_race', {});
    this.gameState.returnToLobby();
    this.loop.resetForNewRace();
    this.hud.showWaitingForRacers(false);
  }

  _spectate(gameId) {
    if (!this.network.isConnected() || !gameId) return;
    this.network.send('spectate_game', { gameId });
  }

  _stopSpectating() {
    this.network.send('stop_spectating', {});
    this.gameState.returnToLobby();
    this.loop.resetForNewRace();
  }
}
